import { LoadingSkeletonList } from '@/components/SkeletonLoading';
import { useGetTransactionItemDetail } from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import { useTransactionItemDetailEdit } from '@/features/transaction_items/Detail/context/TransactionItemDetailEdit';
import { useTranslation } from '@/i18n';
import { ButtonV2 } from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
} from 'react';
import { useParams } from 'react-router-dom';
import { DetailTopView } from './DetailTopView';
import { EditForm } from './EditForm';
import styles from './Top.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  isLoading?: boolean;
  label: string;
  visibility: boolean;
  tooltip_message: string;
  setDetailTopLoading: Dispatch<SetStateAction<boolean>>;
};

export type DetailTopInnerProps = {
  isLoading?: boolean;
  setDetailTopLoading: Dispatch<SetStateAction<boolean>>;
  setTransactionItemData?: (
    data: {
      can_delete?: boolean;
      delete_tooltip?: string;
    } | null
  ) => void;
};

export const DetailTop: FC<Props> = memo(
  ({
    isLoading = false,
    label,
    visibility,
    tooltip_message,
    setDetailTopLoading,
  }) => {
    const { t } = useTranslation();
    const { onEdit } = useTransactionItemDetailEdit();

    useEffect(() => {
      setDetailTopLoading(isLoading);
    }, [isLoading, setDetailTopLoading]);

    const handleEdit = useCallback(() => {
      onEdit();
    }, [onEdit]);

    return (
      <div className={cx(styles['container'])}>
        <div className={cx(styles['button-container'])}>
          <ButtonV2 isSecondary isDisabled={isLoading} onClick={handleEdit}>
            {t('edit')}
          </ButtonV2>
        </div>
        {isLoading ? (
          <LoadingSkeletonList repeatCount={3} />
        ) : (
          <DetailTopView
            label={label}
            visibility={visibility}
            tooltip_message={tooltip_message}
          />
        )}
      </div>
    );
  }
);
DetailTop.displayName = 'DetailTop';

export const DetailTopInner: FC<DetailTopInnerProps> = memo(
  ({ isLoading = false, setDetailTopLoading, setTransactionItemData }) => {
    const { transaction_item_id } = useParams();
    const { isEdit } = useTransactionItemDetailEdit();
    const { data } = useGetTransactionItemDetail(transaction_item_id ?? '');

    useEffect(() => {
      if (setTransactionItemData) {
        setTransactionItemData(
          data
            ? {
                can_delete: data.can_delete,
                delete_tooltip: data.delete_tooltip ?? undefined,
              }
            : null
        );
      }
    }, [data, setTransactionItemData]);

    if (isEdit) {
      return (
        <EditForm
          label={data?.label ?? ''}
          visibility={data?.is_visible ?? false}
          tooltip_message={data?.tooltip_message ?? ''}
        />
      );
    }

    return (
      <DetailTop
        isLoading={isLoading}
        label={data?.label ?? ''}
        visibility={data?.is_visible ?? false}
        tooltip_message={data?.tooltip_message ?? ''}
        setDetailTopLoading={setDetailTopLoading}
      />
    );
  }
);
DetailTopInner.displayName = 'DetailTopInner';

export const DetailTopContainer: FC<{
  setDetailTopLoading: Dispatch<SetStateAction<boolean>>;
  setTransactionItemData?: (
    data: {
      can_delete?: boolean;
      delete_tooltip?: string;
    } | null
  ) => void;
}> = memo(({ setDetailTopLoading, setTransactionItemData }) => {
  return (
    <Suspense
      fallback={
        <DetailTop
          isLoading
          label=''
          visibility={false}
          tooltip_message=''
          setDetailTopLoading={setDetailTopLoading}
        />
      }
    >
      <DetailTopInner
        setDetailTopLoading={setDetailTopLoading}
        setTransactionItemData={setTransactionItemData}
      />
    </Suspense>
  );
});
DetailTopContainer.displayName = 'DetailTopContainer';
